import React, {
  ChangeEvent,
} from "react";
import { Box, InputBase, IconButton, Avatar } from "@mui/material";
import { Typography } from '@mui/material';
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from "@mui/lab";
import { timelineItemClasses } from '@mui/lab/TimelineItem';
import { grey } from '@mui/material/colors';
import { getAvatarName } from "utils/functions";

export interface IActivityListProps {
  title?: string;
  allowingToComment: boolean;
  listItems: any;
  currentUser: any;
  comment?: string;
  onComment?: () => void;
  onChangeComment?: (value: string) => void;
  lastInstanceDemo?: boolean,
  screenDemo: string,
}

const ActivityList = (props: IActivityListProps) => {
  const {
    title = "Activity",
    allowingToComment = false,
    listItems,
    currentUser,
    comment = "",
    onComment = () => {},
    onChangeComment = () => {},
    lastInstanceDemo = true,
    screenDemo= 'desktop',
  } = props;
  const handleChangeComment = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    onChangeComment(value);
  };

  const handleKeydownComment = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const ENTER_KEY = "Enter";
    if (e.key === ENTER_KEY) {
      onComment();
    }
  };

  return (
    <Box
      sx={{
        maxWidth: {
          xs: '100%',
          md: 760
        },
        '& .MuiAvatar-root': {
          width: '100% !important',
          height: '100% !important'
        },
        '& .MuiAvatar-root span': {
          color: 'primary.contrastText',
          fontSize: '12px',
          lineHeight: 1,
          fontWeight: 400,
        },
      }}
    >
      {title && <Typography
        variant="h3"
        color='text.primary'
        mb={4}
      >
        Activity
      </Typography>}
      <Box>
        {allowingToComment && (
          <Box
            sx={{
              display: 'flex' ,
              marginBottom: '20px',
              position: 'relative',
              "&::after": {
                content: '""',
                position: 'absolute',
                width: '2px',
                height: {
                  xs: 'calc(100% - 12px)',
                  md: screenDemo === 'small screen' ? 'calc(100% - 12px)': 'calc(100% - 16px)'
                },
                backgroundColor: grey[300],
                bottom: {
                  xs: '-20px',
                  md: screenDemo === 'small screen' ? '-20px': '-24px'
                },
                left: {
                  xs: '22px',
                  md: screenDemo === 'small screen' ? '22px': '49px'
                },
                transform: 'translateX(-50%)',
              },
              "& .MuiAvatar-root span": {
                fontSize: {
                  xs: '12px',
                  md: screenDemo === 'small screen' ? '12px': '16px'
                },
              },
            }} 
          >
            <Box 
              sx={{
                width: {
                  xs: 44,
                  md: screenDemo === 'small screen' ? 44: 98
                },
                display: 'flex',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  overflow: "hidden",
                  width: {
                    xs: 24,
                    md: screenDemo === 'small screen' ? 24: 32
                  },
                  height: {
                    xs: 24,
                    md: screenDemo === 'small screen' ? 24: 32
                  },
                  borderRadius: "50%"
                }}
              >
                <Avatar
                  sx={{ bgcolor: grey[400], fontSize: {xs: '12px', md: screenDemo === 'small screen' ? '12px': '16px'}, lineHeight: 1 }}
                  alt={currentUser?.name}
                  src="/broken-image.jpg"
                  children={getAvatarName(currentUser?.name)}
                />
              </Box>
            </Box>
            <Box
              sx={{
                width: {
                  xs: 'calc(100% - 44px)',
                  md: screenDemo === 'small screen' ? 'calc(100% - 44px)': 'calc(100% - 98px)'
                },  
                position: 'relative',
              }}
            >
              <InputBase
                value={comment}
                placeholder={'Leave a comment'}
                inputProps={{ "aria-label": 'Leave a comment' }}
                onChange={handleChangeComment}
                onKeyDown={handleKeydownComment}
                sx={{
                  border: '1px solid',
                  borderColor: grey[400],
                  borderRadius: '4px',
                  padding: '8px 12px',
                  width: '100%',
                  '& input': {
                    padding: '0',
                    fontSize: '16px',
                    lineHeight: '24px',
                    fontWeight: '400',
                    letterSpacing: '0.15px',
                    color: 'text.secondary',
                  }
                }}
              />
              <IconButton
                type="button"
                aria-label="search"
                onClick={onComment}
                sx={{
                  position: 'absolute !important',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  padding: '0 !important',
                }}
              >
                <SendRoundedIcon />
              </IconButton>            
            </Box>
          </Box> )
        }
        <Timeline
          sx={{
            margin: 0,
            padding: 0,
            [`& .${timelineItemClasses.root}:before`]: {
              flex: 0,
              padding: 0,
            },
          }}
        >
          {listItems && listItems.length > 0 && (
            listItems.map((item : any, index: any) => {
              return (
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot 
                      variant="outlined"
                      sx={{
                        width: {
                          xs: 44,
                          md: screenDemo === 'small screen' ? 44: 98
                        },
                        border: 'none',
                        margin: `${index !== 0 ? '8px 0' : '9px 0 0'}`,
                        padding: 0,
                        display: 'flex',
                        justifyContent: 'center', 
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          overflow: "hidden",
                          width: 24,
                          height: 24,
                          borderRadius: "50%"
                        }}
                      >
                        {item.userName ? (
                          <Avatar
                            sx={{ bgcolor: grey[400], fontSize: '12px', lineHeight: 1 }}
                            alt={item.userName}
                            src="/broken-image.jpg"
                            children={getAvatarName(item.userName)}
                          /> ) : 
                          item.deviceName ? (
                            <Avatar
                              alt={item.deviceName}
                              src={item.deviceImage || "/broken-image.jpg"}
                            />
                          ) : item.orderNumber ? (
                            <Box 
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: grey[400]
                              }}
                            />
                          ) : ''}
                      </Box>
                    </TimelineDot>
                    {!lastInstanceDemo ? (
                      <TimelineConnector
                        sx={{
                          backgroundColor: grey[300]
                        }}
                      />
                    ) : index !== listItems.length -1 ? (
                      <TimelineConnector
                        sx={{
                          backgroundColor: grey[300]
                        }}
                      />
                    ) : ''}
                  </TimelineSeparator>
                  <TimelineContent
                    sx={{
                      padding: 0,
                      marginBottom: 2,
                    }}
                  >
                    <Box 
                      sx={{
                        my: 1,                       
                      }}
                    >
                      <Typography
                        variant="h6"
                        color='text.primary'
                        component='span'
                        sx={{
                          mr: '4px',
                        }}
                      >
                        {item.userName ? item.userName : item.deviceName ? item.deviceName : item.orderNumber ? `Order #${item.orderNumber}` :'' }
                      </Typography>
                      <Typography
                        variant="body1"
                        color='text.secondary'
                        component='span'
                        sx={{
                          mr: '4px',
                        }}
                      >
                        {item.activity}
                      </Typography>
                      <Typography
                        variant="h6"
                        color='text.primary'
                        component='span'
                      >
                        {item.activity === 'uploaded file' ? item.uploadedFileName : item.activity === 'issued an Order Request to' ? item.approverName : (item.userName && item.deviceName) ? item.deviceName : ''}
                      </Typography>
                    </Box>
                    {item.activity === 'commented' && (
                      <Box
                        sx={{
                          backgroundColor: 'inherit.grayContainer',
                          p: 2, 
                          borderRadius: '8px',
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="body1"
                          color='text.primary'
                        >
                          {item.commentContent}
                        </Typography>
                      </Box>)
                    }
                    <Typography
                      variant="body2"
                      color='text.secondary'
                    >
                      {item.activityTime}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              )
            })
          )}
        </Timeline>
      </Box>
    </Box>
  );
};

export default ActivityList;
